import { AccountKindService } from '@frontendlib/common/services/account-kind.service';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { inject } from '@angular/core';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';
import { itemFollowStoreFor } from '@modules/common/services/item-follow/item-follow-store';
import { clearItemFollows } from '@modules/common/services/item-follow/item-follow-messages';
import { PURGE_REFUSED_REASON } from '@modules/common/pipes/can-purge.pipe';
// Common
import { Component } from '@angular/core';

// Utils
import { Like } from '@modules/common/types/like';
import { checkExhaustiveness } from '@modules/common/utils/switch';

// Types
import { Application } from '@modules/common/types/application';
import { File } from '@modules/files/types/file';
import { Folder } from '@modules/files/types/folder';
import { VirtualFolder } from '@modules/files/types/virtual-folder';

// Services
import { FilesService } from '@modules/files/services/files.service';
import { FoldersService } from '@modules/files/services/folders.service';

// Components
import { BaseSidebarContextMenuComponent } from '@modules/common/components/base-sidebar-context-menu/base-sidebar-context-menu.component';
import { StitchType } from '@sharedlib/common/types/StitchType';

const PROTOTYPE_MENU_KEYS: ReadonlyArray<VirtualFolder> = ['all_files', 'assigned_to_me', 'flagged'];

@Component({
  selector: 'app-sidebar-context-menu',
  templateUrl: './sidebar-context-menu.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-context-menu/base-sidebar-context-menu.component.less',
    './sidebar-context-menu.component.less',
  ],
  standalone: false,
})
export class SidebarContextMenuComponent extends BaseSidebarContextMenuComponent<
  StitchType.FILE,
  StitchType.FOLDER,
  VirtualFolder
> {
  protected readonly followUpWording = injectFollowUpWording();
  protected readonly PURGE_REFUSED_REASON = PURGE_REFUSED_REASON;

  advancedSearchStateKey = Application.files;
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly accountKind = inject(AccountKindService);
  /** The rows the Team prototype draws one way: New file, New folder, (Unflag all,) View settings…. */
  protected get prototypeMenu(): boolean {
    return PROTOTYPE_MENU_KEYS.includes(this.sidebarFilterKey);
  }
  protected get insideTeam(): boolean {
    return this.accountKind.isTeam() && this.spaceContextStore.insideTeam();
  }
  protected get isTeamRail(): boolean {
    return this.tenantId != null && this.accountKind.isTeam() && this.spaceContextStore.insideTeam();
  }
  private readonly followToaster = inject(ToasterService);
  protected clearMySubscriptions(): void {
    clearItemFollows(itemFollowStoreFor(this.itemsService), this.tenantId, this.followToaster).subscribe();
  }

  /** "View settings…" on this row: the card for the list the row opens, for every login. */
  protected openRowViewSettings(): void {
    this.openViewSettings(this.sidebarFilterKey === 'all_folders' ? 'folders' : 'files');
  }

  constructor(filesService: FilesService, foldersService: FoldersService) {
    super(filesService, foldersService);
  }

  protected getCommonParams(): Like<File | Folder> {
    let params: Like<File | Folder> = {};

    switch (this.sidebarFilterKey) {
      case 'following':
      case 'assigned_to_me':
      case 'search_results':
      case 'all_files':
      case 'all_folders':
        break;
      case 'snoozed':
      case 'archived':
      case 'deleted':
      case 'flagged':
      case 'followed':
        params = super.getCommonParams();
        break;
      default:
        checkExhaustiveness(this.sidebarFilterKey);
    }

    return params;
  }

  protected stitchItemFactory(params: Partial<File>): File {
    return new File({ ...params, folderId: this.selectedContainerStitchItemId });
  }

  protected containerStitchItemFactory(params: Partial<Folder>): Folder {
    return new Folder({ ...params, parentId: this.selectedContainerStitchItemId });
  }
}
